// =============================================================================
// FILE: packages/renderer/src/components/growth/GrowthCelebration.tsx
// PURPOSE: Emmy Growth Agent toast — fires whenever R² completes a meaningful
//          action. Slides in from bottom-right, highlights the business value,
//          and drains away in 5.5 seconds.
// =============================================================================

import { useEffect, useRef, type JSX } from 'react';
import { createPortal } from 'react-dom';
import { X, Sparkles } from 'lucide-react';
import { useGrowthAgentStore } from '../../stores/growthAgent.store.js';

const AUTO_DISMISS_MS = 5500;

const TIER_BORDER: Record<string, string> = {
  milestone: '2px',
  progress:  '2px',
  routine:   '1px',
};

export function GrowthCelebration(): JSX.Element | null {
  const { celebration, dismiss } = useGrowthAgentStore();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!celebration) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(dismiss, AUTO_DISMISS_MS);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [celebration?.id, dismiss]);   // key on id so re-triggers reset timer

  if (!celebration) return null;

  const { headline, message, value, accentColor, tier } = celebration;

  return createPortal(
    <div
      data-testid="growth-celebration-toast"
      style={{
        position:     'fixed',
        bottom:       28,
        right:        28,
        zIndex:       10000,
        width:        340,
        background:   'rgba(6,10,20,0.97)',
        border:       `1px solid ${accentColor}44`,
        borderLeft:   `${TIER_BORDER[tier] ?? '2px'} solid ${accentColor}`,
        borderRadius: 10,
        boxShadow:    `0 10px 40px rgba(0,0,0,0.7), 0 0 60px ${accentColor}14`,
        overflow:     'hidden',
        animation:    'gc-slide-in 0.35s cubic-bezier(0.34,1.56,0.64,1)',
      }}
    >
      <style>{`
        @keyframes gc-slide-in {
          from { transform: translateY(24px) scale(0.96); opacity: 0; }
          to   { transform: translateY(0)    scale(1);    opacity: 1; }
        }
        @keyframes gc-drain {
          from { width: 100%; }
          to   { width: 0%; }
        }
      `}</style>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 12px 8px 14px' }}>
        <div style={{
          width: 30, height: 30, borderRadius: 8, flexShrink: 0, marginTop: 1,
          background: `${accentColor}18`,
          border: `1px solid ${accentColor}44`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Sparkles style={{ width: 14, height: 14, color: accentColor }} />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 9, letterSpacing: '0.20em', textTransform: 'uppercase',
            color: `${accentColor}bb`, marginBottom: 2,
          }}>
            Emmy · Growth Agent
          </div>
          <div style={{
            fontSize: 12.5, fontWeight: 700,
            color: 'rgb(240,245,255)',
            lineHeight: 1.3,
          }}>
            {headline}
          </div>
        </div>

        <button
          type="button"
          onClick={dismiss}
          data-testid="growth-celebration-dismiss"
          style={{
            width: 22, height: 22, flexShrink: 0, marginTop: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: 'rgba(148,163,184,0.4)', borderRadius: 4,
            transition: 'color 0.15s',
          }}
          onMouseOver={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(226,232,240,0.8)'; }}
          onMouseOut={(e)  => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(148,163,184,0.4)'; }}
        >
          <X style={{ width: 11, height: 11 }} />
        </button>
      </div>

      {/* ── Body ── */}
      <div style={{ padding: '0 14px 12px' }}>
        <div style={{
          fontSize: 11.5, color: 'rgba(226,232,240,0.68)',
          lineHeight: 1.55, marginBottom: 6,
        }}>
          {message}
        </div>
        <div style={{
          fontSize: 10.5, color: accentColor, opacity: 0.82,
          fontStyle: 'italic', lineHeight: 1.45,
          borderLeft: `2px solid ${accentColor}44`,
          paddingLeft: 8,
        }}>
          {value}
        </div>
      </div>

      {/* ── Auto-dismiss drain bar ── */}
      <div style={{ height: 2, background: `${accentColor}1a` }}>
        <div
          key={celebration.id}       // key forces re-mount → restarts animation
          style={{
            height: '100%',
            background: `linear-gradient(90deg, ${accentColor}, ${accentColor}66)`,
            animation: `gc-drain ${AUTO_DISMISS_MS}ms linear forwards`,
          }}
        />
      </div>
    </div>,
    document.body,
  );
}
